import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./SmartDPMapMarker.scss";
import { resolutionPropType } from "app/utils/propTypes";
import get from "lodash/get";
import "./SmartDPMap.scss";
import {
	DEFAULT_MAP_ZOOM,
	FOCUS_MAP_ZOOM,
	MAP_FIT_MAX_ZOOM,
	MAP_TOP_PRODUCTS_COUNT,
	QUOTATION_STATUS_VALIDE,
	RESOLUTION,
	SDP_PRODUCTS_FILTERS_KEY,
} from "app/constants";
import { getBoundsOfPoints, isPointInBounds } from "app/pages/.shared/ProductsMap/mapBounds";
import SmartDPMapList from "app/pages/SmartDP/Map/SmartDPMapListing/SmartDPMapList";
import { FormattedMessage } from "react-intl";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import { isServerSide } from "app/utils/utils";
import ProductsMap from "app/pages/.shared/ProductsMap/ProductsMap";
import { Button } from "app/components/ui/Button/Button";
import { useFacets, useFilterParam } from "app/features/product/productFilters";
import {
	FLAGSHIP_EVENT_ON_SORT_SELECTION,
	useSortContext,
	useSortedProducts,
} from "app/features/product/productSort";
import { useLocation, useNavigate } from "react-router";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { ArrowLeftIcon, CrossIcon } from "app/icons/navigation";
import { HotelFilledIcon, ZoneFilledIcon } from "app/icons/travel";
import SmartDPMapItem from "app/pages/SmartDP/Map/SmartDPMapItem";
import { getSmartDPMapInfo } from "app/pages/SmartDP/Map/useSmartDPMapInfo";
import AppGlobalsContext from "app/AppGlobalsContext";

const SDP_FILTERS = Object.values(SDP_PRODUCTS_FILTERS_KEY);

const OBSERVER_OPTIONS = {
	rootMargin: "-1px 0px 0px 0px",
};

export const SmartDPMap = ({
	isStopOver = false,
	resolution,
	onAccommodationClick,
	getAccommodationQuoteLink,
	onAccommodationQuoteLinkClick,
	fetchSDPProductsStatus,
	spotlightAccommodation = {},
	unavailableAccommodation = {},
	specialOfferInfos,
	hasSdpSpecialOffer,
	handleToggleMap = () => {},
	quotationItems = [],
	destinationLabel,
	destinationResortCode,
}) => {
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const navigate = useNavigate();
	const { search } = useLocation();
	const { track } = useAnalytics();
	const { sendHits } = useFlagship();
	const { filteredProducts: filteredAccommodations } = useFacets();
	const { filters, selectedFiltersCount } = useFilterParam(SDP_FILTERS);
	const { sortBy } = useSortContext();
	const [accommodationSelected, setAccommodationSelected] = useState(null);
	const { brand, isUserConnected, strategy } = useContext(AppGlobalsContext);
	const mapRef = useRef();
	const sdpMapRef = useRef(null);

	// Tracks whether the current map view (zoom/center) comes from the user manually
	// zooming/panning, as opposed to a programmatic fitToTopProducts() call, so that a
	// new search on the same destination doesn't clobber the user's chosen view.
	const isAutoFittingRef = useRef(false);
	const hasUserManuallyZoomedRef = useRef(false);
	const previousDestinationResortCodeRef = useRef(destinationResortCode);

	const isLoading = !QUOTATION_STATUS_VALIDE.includes(fetchSDPProductsStatus);

	const { sortedProducts: sortedAccommodations } = useSortedProducts(filteredAccommodations);

	// Bounds of the currently visible map frame, lifted from ProductsMap.
	const [mapBounds, setMapBounds] = useState(null);

	// All products (by sort order) inside the current frame: they populate the side list.
	// Recomputed on every zoom/pan, like lastminute.com.
	const productsInFrame = useMemo(() => {
		if (!mapBounds) {
			return sortedAccommodations;
		}
		return sortedAccommodations.filter(
			product =>
				product.gps &&
				isPointInBounds(
					{ lat: parseFloat(product.gps.lat), lng: parseFloat(product.gps.lon) },
					mapBounds
				)
		);
	}, [sortedAccommodations, mapBounds]);

	// The first N in-frame products get the full price vignette on the map;
	// every product after them is a mini marker.
	const topProducts = useMemo(
		() => productsInFrame.slice(0, MAP_TOP_PRODUCTS_COUNT),
		[productsInFrame]
	);

	const topProductIds = useMemo(
		() => new Set(topProducts.map(product => product.id)),
		[topProducts]
	);

	const isSelectedAccommodationVisible = useMemo(
		() =>
			accommodationSelected !== null &&
			productsInFrame.some(accommodation => accommodation.id === accommodationSelected.id),
		[accommodationSelected, productsInFrame]
	);

	const sdpListingTitle = isStopOver ? (
		<>
			<HotelFilledIcon width={16} heigth={16} aria-hidden={true} focusable={false} />
			<FormattedMessage
				id="sdp.listing.stopover.title"
				values={{
					city: destinationLabel,
					count: productsInFrame.length,
				}}
			/>
		</>
	) : (
		<FormattedMessage
			id="listing.title"
			values={{
				count: productsInFrame.length,
			}}
		/>
	);

	useEffect(() => {
		if (selectedFiltersCount === 0) {
			if (spotlightAccommodation?.id && sortedAccommodations[0]) {
				handleChangeDefaultCenter(sortedAccommodations[0]);
			}
			return;
		}

		track("product_list_filtered", {
			productCount: sortedAccommodations.length,
			filters,
		});
	}, [filters]);

	const [itemSelected, setItemSelected] = useState("");
	const [scrollTo, setScrollTo] = useState({ name: "", seq: 0 });

	const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM);

	const [center, setCenter] = useState({
		lat: get(sortedAccommodations, "[0].gps.lat"),
		lng: get(sortedAccommodations, "[0].gps.lon"),
	});

	// A destination change means the previous zoom/center no longer make sense for the
	// new result set, so we forget the user's manual zoom and let the map re-frame.
	useEffect(() => {
		if (previousDestinationResortCodeRef.current !== destinationResortCode) {
			previousDestinationResortCodeRef.current = destinationResortCode;
			hasUserManuallyZoomedRef.current = false;
		}
	}, [destinationResortCode]);

	useEffect(() => {
		if (itemSelected) {
			const accommodationSelected = sortedAccommodations.find(
				accommodation => accommodation.name === itemSelected
			);
			setAccommodationSelected(accommodationSelected);
		}
	}, [itemSelected]);

	const handleMarkerClick = useCallback(name => {
		setItemSelected(name);
		setScrollTo(prev => ({ name, seq: prev.seq + 1 }));
		track("map_pin_clicked", { name: name });
	}, []);

	const handleChangeDefaultCenter = useCallback((accommodation = {}) => {
		setCenter({ lat: accommodation.gps?.lat, lng: accommodation.gps?.lon });
		setItemSelected(accommodation.name);
		setZoom(FOCUS_MAP_ZOOM);
	}, []);

	// Bounds only: the map's own zoom is tracked internally by ProductsMap for clustering.
	// Feeding it back into `zoom` here would re-enter ProductsMap as a controlled prop change
	// on every user gesture, forcing google-map-react to call map.setZoom() and fight the
	// native smooth zoom animation (FBR-4844). `zoom` state is only set for deliberate,
	// programmatic changes (focus on a pin, recenter).
	const handleMapChange = useCallback((_zoom, bounds) => {
		setMapBounds(bounds);
		if (!isAutoFittingRef.current) {
			hasUserManuallyZoomedRef.current = true;
		}
	}, []);

	// Zoom/pan the map (imperatively, to avoid a React-driven re-center) so the frame
	// encloses the whole result set. A maximum zoom is enforced so one far-off product
	// cannot zoom the map out too much. Built on sortedAccommodations (not the
	// frame-dependent topProducts) so a user zoom/pan never triggers a re-fit.
	const fitToTopProducts = useCallback(() => {
		if (!mapRef.current || !window.google?.maps || sortedAccommodations.length === 0) {
			return;
		}
		const coordinates = sortedAccommodations
			.filter(product => product.gps?.lat && product.gps?.lon)
			.map(product => ({
				lat: parseFloat(product.gps.lat),
				lng: parseFloat(product.gps.lon),
			}));
		const box = getBoundsOfPoints(coordinates);
		if (!box) {
			return;
		}
		const latLngBounds = new window.google.maps.LatLngBounds(
			{ lat: box.south, lng: box.west },
			{ lat: box.north, lng: box.east }
		);
		isAutoFittingRef.current = true;
		mapRef.current.fitBounds(latLngBounds, 60);
		window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
			if (mapRef.current && mapRef.current.getZoom() > MAP_FIT_MAX_ZOOM) {
				mapRef.current.setZoom(MAP_FIT_MAX_ZOOM);
			}
			isAutoFittingRef.current = false;
		});
	}, [sortedAccommodations]);

	// If a hovered product sits outside the visible frame, extend the frame just enough
	// to reveal it instead of recentering on it.
	const ensureAccommodationInFrame = useCallback(
		accommodation => {
			if (!mapRef.current || !window.google?.maps || !accommodation?.gps) {
				return;
			}
			const point = {
				lat: parseFloat(accommodation.gps.lat),
				lng: parseFloat(accommodation.gps.lon),
			};
			if (mapBounds && isPointInBounds(point, mapBounds)) {
				return;
			}
			const currentBounds = mapRef.current.getBounds();
			if (!currentBounds) {
				return;
			}
			currentBounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
			mapRef.current.fitBounds(currentBounds, 60);
		},
		[mapBounds]
	);

	const styleMap = !isServerSide
		? {
				width: "100%",
				// tiens compte de la hauteur du header + marketing banner
				height: isMobile
					? "100%"
					: window?.innerHeight - (sdpMapRef.current?.getBoundingClientRect().top || 72),
			}
		: {};

	const selectedAccommodationMapInfo = useMemo(
		() =>
			accommodationSelected
				? getSmartDPMapInfo(accommodationSelected, {
						brand,
						isUserConnected,
						strategy,
					})
				: null,
		[accommodationSelected, brand, isUserConnected, strategy]
	);

	const points = useMemo(
		() =>
			sortedAccommodations
				.map(accommodation => {
					if (accommodation.gps && accommodation.gps.lat) {
						const { tag } = getSmartDPMapInfo(accommodation, {
							brand,
							isUserConnected,
							strategy,
						});

						return {
							type: "Feature",
							properties: {
								id: accommodation.id,
								name: accommodation.name,
								productUrl: accommodation.productUrl,
								isFlashsale: accommodation.isFlashsale,
								total: accommodation.total,
								tag,
								shape: topProductIds.has(accommodation.id) ? "full" : "mini",
							},
							geometry: {
								type: "Point",
								coordinates: [
									parseFloat(accommodation.gps?.lon),
									parseFloat(accommodation.gps?.lat),
								],
							},
						};
					}
					return undefined;
				})
				.filter(accommodation => accommodation),
		[sortedAccommodations, topProductIds, brand, isUserConnected, strategy]
	);

	// When hovering a product card we only highlight its marker and keep the current zoom.
	// If the product is outside the current frame we zoom out just enough to reveal it.
	const handleAccommodationHover = useCallback(
		(accommodation, index) => {
			track("map_product_hovered", {
				name: accommodation?.name,
				index: `${index + 1}`,
			});
			setItemSelected(accommodation.name);
			ensureAccommodationInFrame(accommodation);
		},
		[ensureAccommodationInFrame]
	);

	// eslint-disable-next-line consistent-return
	useEffect(() => {
		if (spotlightAccommodation?.id) {
			// use setTimeout to zoom to pin
			const timer = setTimeout(() => {
				handleChangeDefaultCenter(spotlightAccommodation);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [spotlightAccommodation?.id]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		track("map_displayed", {
			itemCount: `${sortedAccommodations.length}`,
			zoom: `${DEFAULT_MAP_ZOOM}`,
		});
	}, [sortedAccommodations.length]);

	useEffect(() => {
		if (hasUserManuallyZoomedRef.current && !spotlightAccommodation?.id) {
			return;
		}
		setCenter({
			lat: get(sortedAccommodations, "[0].gps.lat"),
			lng: get(sortedAccommodations, "[0].gps.lon"),
		});
	}, [sortedAccommodations.length, isMobile, spotlightAccommodation?.id]);

	// we reset the selection when a new search is triggered
	// we use fetchSDPProductsStatus instead of search because filters update search
	// and we don't want to reset selection on filter update
	useEffect(() => {
		setAccommodationSelected(null);
		setItemSelected("");
	}, [fetchSDPProductsStatus]);

	const handleGoBackToListPage = () => {
		navigate("../" + search, { replace: true });
		track("user_click_back-listing-map");
	};

	const handleSortOpen = () => {
		track("user_click_sort-button", {
			sortType: sortBy,
			viewType: "map",
			filterPosition: "sticky",
		});
	};

	const handleFilterTriggerMobileOpen = () => {
		track("listingPage_filters_mobile_open", {
			viewType: "map",
			filterPosition: "sticky",
		});
	};

	const handleFilterOpen = filterId => {
		track(`listingPage_filter_by_${filterId}_open`, {
			viewType: "map",
		});
	};

	const handleApplyFilters = (selectedFilters, resultsCount) => {
		track("sdp-filters-view-offers-button-clicked", {
			productCount: resultsCount,
			filters: selectedFilters,
		});
	};

	const handleSortChange = sortById => {
		track("sort_product_list", { sortby: sortById });

		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_ON_SORT_SELECTION[sortById],
		});
	};

	const handleItemSelectedClick = useCallback(() => {
		onAccommodationQuoteLinkClick(accommodationSelected);
	}, [onAccommodationQuoteLinkClick, accommodationSelected]);

	const handleMapClick = useCallback(() => {
		setAccommodationSelected(null);
		setItemSelected("");
	}, []);

	const handleMapApiLoaded = useCallback(
		({ map }) => {
			mapRef.current = map;
			// On a spotlight search the map zooms to the spotlight pin (see effect below);
			// otherwise we frame the top products (AC9), unless the user already zoomed
			// manually on this destination and we don't want to clobber their view.
			if (!spotlightAccommodation?.id && !hasUserManuallyZoomedRef.current) {
				fitToTopProducts();
			}
		},
		[spotlightAccommodation?.id, fitToTopProducts]
	);

	// Frame the whole result set when it becomes available or when the sort order changes,
	// as long as there is no spotlight pin driving the view and the user hasn't already
	// zoomed manually on this destination.
	useEffect(() => {
		if (
			!spotlightAccommodation?.id &&
			!hasUserManuallyZoomedRef.current &&
			mapRef.current &&
			sortedAccommodations.length > 0
		) {
			fitToTopProducts();
		}
	}, [fitToTopProducts, spotlightAccommodation?.id, sortedAccommodations.length]);

	const handleRecenterClick = useCallback(() => {
		setItemSelected(spotlightAccommodation.name);
		setScrollTo(prev => ({
			name: spotlightAccommodation.name,
			seq: prev.seq + 1,
		}));
		setAccommodationSelected(spotlightAccommodation);
		// When the user drags, the React center state keeps the spotlight's original lat/lng. Clicking recenter calls setCenter with the exact
		// same values → google-map-react's shouldComponentUpdate returns false → no panTo call.
		// The fix is to bypass the prop and call mapRef.current.panTo() directly via the imperative API. I'll expose onGoogleApiLoaded from ProductsMap so the parent can hold the map reference.
		if (mapRef.current) {
			mapRef.current.panTo({
				lat: spotlightAccommodation.gps?.lat,
				lng: spotlightAccommodation.gps?.lon,
			});
			mapRef.current.setZoom(FOCUS_MAP_ZOOM);
		}

		track("click_return_to_search");
	}, [spotlightAccommodation?.id]);

	const isSelectedAccommodationSpotlighted =
		accommodationSelected?.id === spotlightAccommodation?.id;

	const showRecenterButton = useMemo(() => {
		if (!spotlightAccommodation?.id) {
			return false;
		}
		const isSpotlightInFrame = productsInFrame.some(
			accommodation => accommodation?.id === spotlightAccommodation?.id
		);
		const isSpotlightSelected = accommodationSelected?.id === spotlightAccommodation?.id;
		// Hide only when the spotlight is both visible in the current frame and selected.
		return !(isSpotlightInFrame && isSpotlightSelected);
	}, [productsInFrame, spotlightAccommodation?.id, accommodationSelected?.id]);

	return (
		<div className="sdp-map" ref={sdpMapRef}>
			{isMobile && (
				<ActionBar
					isSortAvailable={true}
					showMiniSearch={true}
					onSortOpen={handleSortOpen}
					onFilterOpen={handleFilterOpen}
					onFilterTriggerMobileOpen={handleFilterTriggerMobileOpen}
					showMapButton={true}
					isLoading={isLoading}
					variant="borderless"
					observerOptions={OBSERVER_OPTIONS}
					onApplyFilters={handleApplyFilters}
					onSortChange={handleSortChange}
				/>
			)}

			{!isMobile && (
				<SmartDPMapList
					specialOfferInfos={specialOfferInfos}
					hasSdpSpecialOffer={hasSdpSpecialOffer}
					itemSelected={itemSelected}
					scrollTo={scrollTo}
					fetchSDPProductsStatus={fetchSDPProductsStatus}
					spotlightAccommodation={spotlightAccommodation}
					unavailableAccommodation={unavailableAccommodation}
					isLoading={isLoading}
					accommodationsList={productsInFrame}
					onAccommodationClick={onAccommodationClick}
					onAccommodationHover={handleAccommodationHover}
					title={sdpListingTitle}
					quotationItems={quotationItems}
				/>
			)}

			{isLoading ? (
				<div className="sdp-map__google-map" style={styleMap}>
					<img
						data-testid="map-loader"
						src="/static/icons/fond-map-DESK.svg"
						alt="map-skeleton"
					/>
				</div>
			) : (
				<div className="sdp-map__google-map" style={styleMap}>
					{isMobile ? (
						<Button
							size="icon-lg"
							startIcon={<ArrowLeftIcon size={20} />}
							data-testid="map-back-button"
							className="sdp-map__go-back-button"
							onClick={handleGoBackToListPage}
						/>
					) : (
						<Button
							variant="secondary"
							startIcon={<CrossIcon />}
							className="sdp-map__google-map-button"
							onClick={handleToggleMap}
						>
							<FormattedMessage id="product.page.list.button.label.long" />
						</Button>
					)}
					<div className="sdp-map__footer">
						{showRecenterButton && (
							<Button
								variant="secondary"
								startIcon={<ZoneFilledIcon />}
								data-testid="map-recenter-button"
								className="sdp-map__google-map-button sdp-map__google-map-button--recenter"
								onClick={handleRecenterClick}
							>
								<FormattedMessage id="sdp.map.recenter.button.label" />
							</Button>
						)}

						{isMobile && (
							<>
								{isSelectedAccommodationVisible && (
									<div
										data-testid="map-selected-item"
										className="sdp-map__item-selected"
									>
										<SmartDPMapItem
											accommodation={accommodationSelected}
											tag={selectedAccommodationMapInfo?.tag}
											quoteLink={getAccommodationQuoteLink(
												accommodationSelected
											)}
											onClick={handleItemSelectedClick}
											isSpotlight={isSelectedAccommodationSpotlighted}
										/>
									</div>
								)}
							</>
						)}
					</div>
					<ProductsMap
						onMarkerClick={handleMarkerClick}
						onMapClick={handleMapClick}
						onGoogleApiLoaded={handleMapApiLoaded}
						center={center}
						points={points}
						onMapChange={handleMapChange}
						itemSelected={itemSelected}
						zoom={zoom}
						enableClustering={false}
					/>
				</div>
			)}

			<PushNotificationContainer />
		</div>
	);
};

SmartDPMap.propTypes = {
	isStopOver: PropTypes.bool,
	specialOfferInfos: PropTypes.object,
	hasSdpSpecialOffer: PropTypes.bool,
	fetchSDPProductsStatus: PropTypes.string,
	spotlightAccommodation: PropTypes.object,
	unavailableAccommodation: PropTypes.object,
	resolution: resolutionPropType,
	onAccommodationClick: PropTypes.func,
	getAccommodationQuoteLink: PropTypes.func,
	onAccommodationQuoteLinkClick: PropTypes.func,
	handleToggleMap: PropTypes.func,
	quotationItems: PropTypes.array,
	destinationLabel: PropTypes.string,
	destinationResortCode: PropTypes.string,
};

export default SmartDPMap;
